// A search-as-you-type recipe picker. Originally built just for Plan the
// week's "Quick add" Recipe field, and now the recipe picker used
// everywhere on the Plan page (day dish rows, Snacks/Preserves/Medicinal,
// and the shared-plan editor) for a consistent lookup experience -- these
// replaced the plain <select>-based MealSelect.jsx, which is gone.
import { useState, useRef, useEffect } from "react";
import { COLORS, FONT_SIZE, RADIUS, SPACE, DINING_OUT } from "../lib/constants";

// Alphabetical by title, whatever order the caller passes (the library is
// loaded in creation order), with "Dining out" kept at the top (feedback #38).
function sortOptions(options) {
  return [...options].sort((a, b) => {
    if ((a.id === DINING_OUT) !== (b.id === DINING_OUT)) return a.id === DINING_OUT ? -1 : 1;
    return (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base", numeric: true });
  });
}

export function RecipeTypeahead({ value, onChange, options, placeholder, colors }) {
  const { line, ink, soft, card } = colors;
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef(null);

  const selected = options.find((r) => r.id === value);

  // Keep the input's text in sync whenever the selection changes from
  // outside (e.g. cleared after "Add to plan", or reset when the dish type
  // filter changes) -- value is the source of truth, query is just its
  // text representation while nothing's being typed.
  useEffect(() => {
    setQuery(selected ? selected.title || "Untitled recipe" : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const trimmed = query.trim().toLowerCase();
  const sorted = sortOptions(options);
  const matches = trimmed ? sorted.filter((r) => (r.title || "").toLowerCase().includes(trimmed)) : sorted;

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery(selected ? selected.title || "Untitled recipe" : "");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selected]);

  function selectRecipe(r) {
    onChange(r.id);
    setQuery(r.title || "Untitled recipe");
    setOpen(false);
  }

  function handleKeyDown(e) {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (matches[highlight]) selectRecipe(matches[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery(selected ? selected.title || "Untitled recipe" : "");
    }
  }

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <input
        type="text"
        className="mp-sans"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (value) onChange(""); // typing invalidates the current pick until a match is chosen again
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        style={{ width: "100%", padding: "var(--space-sm) var(--space-lg)", borderRadius: RADIUS.md, border: `1px solid ${line}`, background: COLORS.card, fontSize: FONT_SIZE.base, color: ink, boxSizing: "border-box" }}
      />
      {open && (matches.length > 0 || trimmed) && (
        <div
          className="mp-sans"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 20,
            marginTop: SPACE.hair,
            background: COLORS.card,
            border: `1px solid ${line}`,
            borderRadius: RADIUS.md,
            maxHeight: 220,
            overflowY: "auto",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          {matches.length === 0 ? (
            <div style={{ padding: "var(--space-sm) var(--space-lg)", fontSize: FONT_SIZE.base, color: soft }}>No matches</div>
          ) : (
            matches.map((r, i) => (
              <div
                key={r.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectRecipe(r);
                }}
                onMouseEnter={() => setHighlight(i)}
                style={{ padding: "var(--space-sm) var(--space-lg)", fontSize: FONT_SIZE.base, color: ink, background: i === highlight ? card : COLORS.card, cursor: "pointer" }}
              >
                {r.title || "Untitled recipe"}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
